import { Autocomplete, TextField, Theme, Box } from '@mui/material';
import { SxProps } from '@mui/system';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import * as React from 'react';
import { useTransition, useState, useRef } from 'react';
import ItemApi from 'src/api/ItemApi';
import useVendorBooth from 'src/hooks/useVendorBooth';
import { Item } from 'src/types/itemList';

type TypeAheadProps = {
  sx?: SxProps<Theme>;
  onChange: (value: Item) => void;
  style?: React.CSSProperties;
  vendorNumber: number;
};

const ItemTypeAhead: React.FC<React.PropsWithChildren<TypeAheadProps>> = ({ onChange, sx, style, vendorNumber }) => {
  const currentSearchString = useRef<string>('');
  const [, startTransition] = useTransition();
  const { vendorBooth } = useVendorBooth();
  const [items, setItems] = useState<Item[]>([]);

  React.useEffect(() => {
    if (items.length === 0 && vendorBooth.startingItems.length > 0) {
      setItems([...(vendorBooth.startingItems ?? [])]);
    }
  }, [vendorBooth.startingItems, vendorBooth.startingItems.length, items.length]);

  const filters: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [
      {
        field: 'VendorNumber',
        operator: 'eq',
        value: vendorNumber,
      },
    ],
  };

  const onInputChange = (searchText: string) => {
    currentSearchString.current = searchText;

    if (searchText == null || searchText.length < 1) {
      startTransition(() => {
        setItems([...(vendorBooth.startingItems ?? [])]);
      });
      return;
    }

    ItemApi.getItems(
      {
        take: 25,
        filter: filters,
      } as State,
      searchText
    ).then((result) => {
      if (searchText === currentSearchString.current) {
        startTransition(() => {
          setItems(result.value);
        });
      }
    });
  };

  return (
    <Autocomplete
      sx={sx}
      options={items}
      filterOptions={(x) => x}
      multiple={false}
      autoComplete
      style={style}
      onChange={(e, newValue) => {
        e.stopPropagation();
        onChange(newValue as Item);
      }}
      onInputChange={(e, newValue) => {
        e.stopPropagation();
        onInputChange(newValue);
      }}
      getOptionLabel={(option: Item) => `${option?.itemNumber} - ${option?.name} `}
      renderOption={(p, option: Item) => {
        return (
          <div key={option?.itemNumber ?? '0'}>
            <Box component="li" {...p} id={'item_' + option.itemNumber}>
              {`${option?.itemNumber} - ${option?.name}`}
            </Box>
          </div>
        );
      }}
      renderInput={(params) => (
        <>
          <TextField {...params} name="ItemTypeAhead" label="Search items..." />
        </>
      )}
    />
  );
};

export default ItemTypeAhead;
